/**
 * surveyDataMapper
 *
 * Organises a flat questionnaire answers blob + question metadata into a
 * hierarchical structure: Section → Screen → AnswerItem.
 *
 * This module has no framework dependencies and can be shared as-is with any
 * JavaScript/TypeScript consumer (e.g. innotaxes.com).
 *
 * --- Quick start for external consumers ---
 *
 *   import { buildSectionData } from './surveyDataMapper';
 *
 *   // Both fields come directly from GET /admin/review/flow-sessions/{uuid}
 *   const sections = buildSectionData(session.answers, session.question_metadata);
 *
 * The backend builds question_metadata server-side from the screen config, so
 * the consumer never needs to load or ship FIGMA_ALL_SCREENS.json.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface QuestionMeta {
  question_text: string;
  screen_id: string;
  screen_title: string;
  field_type: string;
  category: string;
  screen_order: number;
}

export interface AnswerItem {
  key: string;
  value: unknown;
  meta: QuestionMeta | null;
}

export interface ScreenGroup {
  screenId: string;
  screenTitle: string;
  order: number;
  items: AnswerItem[];
}

export interface Section {
  id: string;
  label: string;
  screens: ScreenGroup[];
}

// ---------------------------------------------------------------------------
// Section definitions
//
// Each entry maps a logical section ID + display label to a list of lowercase
// category strings that flow-engine screens can produce.  The first matching
// section wins; unmatched screens fall into "Other / System Fields".
// ---------------------------------------------------------------------------

export interface SectionDef {
  id: string;
  label: string;
  matchers: string[];
}

export const SECTION_DEFS: SectionDef[] = [
  { id: 'start', label: 'Start', matchers: ['onboarding', 'getting started', 'getting-started', 'other'] },
  { id: 'my-information', label: 'My Information', matchers: ['personal-info', 'personal info', 'personal', 'family', 'residency', 'my info'] },
  { id: 'income', label: 'Income', matchers: ['income', 'housing'] },
  { id: 'deductions', label: 'Deductions', matchers: ['deduction'] },
  { id: 'foreign-reporting', label: 'Foreign Reporting', matchers: ['foreign'] },
  { id: 'summary', label: 'Summary', matchers: ['summary', 'review'] },
  { id: 'submission', label: 'Submission', matchers: ['submission', 'payment'] },
];

// ---------------------------------------------------------------------------
// Core functions
// ---------------------------------------------------------------------------

/**
 * Maps a category string from question metadata to a section ID.
 * Falls back to 'start' when no matcher is found.
 */
export function resolveSection(category: string, defs: SectionDef[] = SECTION_DEFS): string {
  const c = (category || '').toLowerCase().trim();
  for (const s of defs) {
    for (const m of s.matchers) {
      if (c === m || c.includes(m)) return s.id;
    }
  }
  return 'start';
}

/**
 * Organises a flat answers dict + question_metadata into Section[].
 *
 * @param answers         - The full answers object from FlowState (key → value).
 * @param questionMeta    - Metadata for each answer key (question text, screen, category, …).
 * @param sectionDefs     - Optional override for section definitions.
 * @returns               Ordered array of sections, each containing ordered screens and items.
 */
export function buildSectionData(
  answers: Record<string, unknown>,
  questionMeta: Record<string, QuestionMeta> = {},
  sectionDefs: SectionDef[] = SECTION_DEFS,
): Section[] {
  const screenMap: Record<string, ScreenGroup> = {};

  for (const [key, value] of Object.entries(answers)) {
    const qm = questionMeta[key] ?? null;
    const sid = qm?.screen_id || '__ungrouped__';
    if (!screenMap[sid]) {
      screenMap[sid] = {
        screenId: sid,
        screenTitle: qm?.screen_title || '',
        order: qm?.screen_order ?? 999,
        items: [],
      };
    }
    screenMap[sid].items.push({ key, value, meta: qm });
  }

  const sectionBuckets: Record<string, ScreenGroup[]> = {};
  for (const sg of Object.values(screenMap)) {
    const cat = sg.items[0]?.meta?.category || '';
    const secId = sg.screenId === '__ungrouped__' ? '__ungrouped__' : resolveSection(cat, sectionDefs);
    if (!sectionBuckets[secId]) sectionBuckets[secId] = [];
    sectionBuckets[secId].push(sg);
  }

  for (const bucket of Object.values(sectionBuckets)) {
    bucket.sort((a, b) => a.order - b.order);
  }

  const result: Section[] = [];
  for (const def of sectionDefs) {
    const screens = sectionBuckets[def.id];
    if (screens && screens.length > 0) {
      result.push({ id: def.id, label: def.label, screens });
    }
  }

  const ungrouped = sectionBuckets['__ungrouped__'];
  if (ungrouped && ungrouped.length > 0) {
    result.push({ id: 'other', label: 'Other / System Fields', screens: ungrouped });
  }

  return result;
}

